import type React from 'react';
import { useState } from 'react';
import {
  UiMenu,
  UiMenuButton,
  UiMenuList,
  UiMenuItem,
  UiMenuDivider,
  UiMenuGroup,
  UiButton,
  UiHStack,
  UiText,
  UiBadge,
  UiIconCaretDown,
  UiIconPlus,
  UiIconFloppyDisk,
  UiIconCopy,
  UiIconX,
  UiIconStar,
  UiIconUsers,
  UiIconUser,
  UiBox
} from '@/lib/ui';
import { type UseSavedViewsReturn } from '@/lib/hook/useSavedViews';
import { SaveViewModal } from './SaveViewModal';

interface SavedViewsDropdownProps {
  savedViews: UseSavedViewsReturn
  disabled?: boolean
}

export const SavedViewsDropdown: React.FC<SavedViewsDropdownProps> = ({
  savedViews,
  disabled = false
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'saveAs' | 'new'>('new');

  const {
    views,
    personalViews,
    tenantViews,
    currentView,
    isDirty,
    isLoading,
    loadView,
    deleteView,
    setAsDefault,
    clearCurrentView,
    createNewView
  } = savedViews;

  const handleOpenModal = (mode: 'saveAs' | 'new') => {
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSaveView = async (name: string, scope: 'personal' | 'tenant', isDefault: boolean) => {
    if (modalMode === 'new') {
      // For new view, create with blank state
      await createNewView(name, scope);
    } else {
      // For save as, use current configuration
      await savedViews.saveAsView(name, scope);
    }

    if (isDefault && savedViews.currentView) {
      await setAsDefault(savedViews.currentView.id);
    }

    handleCloseModal();
  };

  const handleDeleteView = async (viewId: number) => {
    if (window.confirm('Are you sure you want to delete this view?')) {
      await deleteView(viewId);
    }
  };

  const getButtonLabel = () => {
    if (currentView) {
      return (
        <UiHStack spacing={2} align="center">
          <span>{currentView.name}</span>
          {isDirty && (
            <UiBadge size="sm" colorScheme="orange" variant="subtle">
              Modified
            </UiBadge>
          )}
        </UiHStack>
      );
    }
    return 'Select view';
  };

  return (
    <>
      <UiMenu>
        <UiMenuButton
          as={UiButton}
          rightIcon={<UiIconCaretDown color="primary.500" />}
          variant="outline"
          size="sm"
          disabled={disabled || isLoading}
          minW="200px"
        >
          {getButtonLabel()}
        </UiMenuButton>

        <UiMenuList maxH="400px" overflowY="auto">
          {/* Current View Actions */}
          {currentView && (
            <>
              <UiMenuGroup title="Current view" fontSize="lg" fontWeight={600}>
                <UiMenuItem
                  icon={<UiIconFloppyDisk size="md" />}
                  onClick={async () => { return await savedViews.saveView(); }}
                  isDisabled={!currentView || !currentView.canEdit || !isDirty}
                >
                  <UiText>Save</UiText>
                  {!isDirty && <UiText as="span" fontSize="xs" color="gray.500" ml={2}>(No changes)</UiText>}
                </UiMenuItem>

                <UiMenuItem
                  icon={<UiIconCopy size="md" />}
                  onClick={() => { return handleOpenModal('saveAs'); }}
                  isDisabled={!currentView}
                >
                  <UiText>Save As...</UiText>
                </UiMenuItem>

                <UiMenuItem
                  icon={<UiIconStar size="md" weight="fill" />}
                  onClick={async () => { return await setAsDefault(currentView.id); }}
                  isDisabled={currentView.isDefault}
                >
                  <UiText>Set as Default</UiText>
                </UiMenuItem>

                {currentView.canDelete && (
                  <UiMenuItem
                    icon={<UiIconX size="md" />}
                    onClick={async () => { return await handleDeleteView(currentView.id); }}
                    color="red.500"
                  >
                    <UiText color="red.500">Delete View</UiText>
                  </UiMenuItem>
                )}
              </UiMenuGroup>
              <UiMenuDivider />
            </>
          )}

          {/* New View */}
          <UiMenuItem
            icon={<UiIconPlus size="md" />}
            onClick={() => { return handleOpenModal('new'); }}
          >
            <UiText>Create New View</UiText>
          </UiMenuItem>

          {views.length > 0 && (
            <>
              <UiMenuDivider />

              {/* Personal Views */}
              {personalViews.length > 0 && (
                <UiMenuGroup title="Personal views" fontSize="lg" fontWeight={600}>
                  {personalViews.map(view => {
                    return (
                      <UiMenuItem
                        key={view.id}
                        onClick={() => { return loadView(view.id); }}
                        justifyContent="space-between"
                        bg={currentView?.id === view.id ? 'primary.50' : undefined}
                      >
                        <UiHStack spacing={2} align="center">
                          <UiText>{view.name}</UiText>
                          {view.isDefault && (
                            <UiIconStar size="md" color="yellow.500" weight="fill" />
                          )}
                        </UiHStack>
                      </UiMenuItem>
                    );
                  })}
                </UiMenuGroup>
              )}

              {/* Tenant Views */}
              {tenantViews.length > 0 && (
                <>
                  {personalViews.length > 0 && <UiMenuDivider />}
                  <UiMenuGroup title="Team views" fontSize="lg" fontWeight={600}>
                    {tenantViews.map(view => {
                      return (
                        <UiMenuItem
                          key={view.id}
                          onClick={() => { return loadView(view.id); }}
                          justifyContent="space-between"
                          bg={currentView?.id === view.id ? 'primary.50' : undefined}
                        >
                          <UiHStack spacing={2} align="center">
                            <UiText>{view.name}</UiText>
                            {view.ownerName && (
                              <UiText color="gray.500">
                              by {view.ownerName}
                              </UiText>
                            )}
                          </UiHStack>
                        </UiMenuItem>
                      );
                    })}
                  </UiMenuGroup>
                </>
              )}
            </>
          )}

          {/* Clear View */}
          {currentView && (
            <>
              <UiMenuDivider />
              <UiMenuItem onClick={() => { return clearCurrentView(true); }}>
                <UiText>Clear View (Show All)</UiText>
              </UiMenuItem>
            </>
          )}
        </UiMenuList>
      </UiMenu>

      {/* Save View Modal */}
      <SaveViewModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveView}
        mode={modalMode}
        currentView={currentView}
        existingViewNames={views.map(v => { return v.name; })}
      />
    </>
  );
};
